import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Plus, Loader2, Trash2, X, Eye, EyeOff, Save } from "lucide-react";

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("af_token")}`,
});

const TAG_OPTIONS = [
  { value: "feature", label: "✨ Feature" },
  { value: "improvement", label: "⚡ Improvement" },
  { value: "fix", label: "🐛 Fix" },
  { value: "announcement", label: "📣 Announcement" },
];

const blankForm = {
  title: "",
  emoji: "✨",
  body: "",
  tag: "feature",
  cta_link: "",
  cta_label: "",
  is_published: true,
};

export default function AdminChangelog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // entry id being edited, "new" for create
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/changelog/admin`, { headers: authHeader() });
      setEntries(r.data?.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const seed = async () => {
    if (!confirm("Seed the historical changelog (last 8 shipped features)? Existing titles are skipped.")) return;
    setSeeding(true);
    try {
      const r = await axios.post(`${API}/changelog/admin/seed`, {}, { headers: authHeader() });
      toast.success(`+${r.data?.total_created || 0} added`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const startEdit = (entry) => {
    setEditing(entry.id);
    setForm({
      title: entry.title,
      emoji: entry.emoji,
      body: entry.body,
      tag: entry.tag,
      cta_link: entry.cta_link || "",
      cta_label: entry.cta_label || "",
      is_published: entry.is_published,
    });
  };

  const startNew = () => {
    setEditing("new");
    setForm(blankForm);
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(blankForm);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/changelog/admin`, form, { headers: authHeader() });
        toast.success("Entry created");
      } else {
        await axios.patch(`${API}/changelog/admin/${editing}`, form, { headers: authHeader() });
        toast.success("Entry updated");
      }
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (entry) => {
    try {
      await axios.patch(
        `${API}/changelog/admin/${entry.id}`,
        { is_published: !entry.is_published },
        { headers: authHeader() },
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const remove = async (entry) => {
    if (!confirm(`Delete "${entry.title}"?`)) return;
    try {
      await axios.delete(`${API}/changelog/admin/${entry.id}`, { headers: authHeader() });
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-changelog">
      <div className="card p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm text-[var(--ink-2)]">
              <Sparkles size={14} /> Changelog
            </div>
            <h2 className="font-display font-bold text-2xl mt-1">What&apos;s New entries</h2>
            <p className="text-sm text-[var(--ink-2)] mt-1 max-w-2xl">
              Every entry here shows up in the slide-out drawer 🎁 in the
              top-right of the app. Users see a red dot until they open it.
              Use this to celebrate every ship — turns updates into
              re-engagement moments.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={seed}
              disabled={seeding}
              data-testid="seed-changelog-btn"
              className="btn-secondary text-sm flex items-center gap-2"
            >
              {seeding ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Seed historical
            </button>
            <button
              onClick={startNew}
              data-testid="new-entry-btn"
              className="btn-primary text-sm flex items-center gap-2"
            >
              <Plus size={14} /> New entry
            </button>
          </div>
        </div>
      </div>

      {editing && (
        <div className="card p-5" data-testid="entry-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold text-lg">
              {editing === "new" ? "New entry" : "Edit entry"}
            </h3>
            <button onClick={cancelEdit} className="p-1 hover:bg-slate-100 rounded">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-1">
              <label className="text-xs text-[var(--ink-2)]">Emoji</label>
              <input
                value={form.emoji}
                onChange={(e) => setForm({ ...form, emoji: e.target.value })}
                data-testid="form-emoji"
                className="w-full mt-1 px-3 py-2 rounded-lg border text-xl text-center"
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs text-[var(--ink-2)]">Title *</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="form-title"
                className="w-full mt-1 px-3 py-2 rounded-lg border text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-[var(--ink-2)]">Tag</label>
              <select
                value={form.tag}
                onChange={(e) => setForm({ ...form, tag: e.target.value })}
                data-testid="form-tag"
                className="w-full mt-1 px-3 py-2 rounded-lg border text-sm"
              >
                {TAG_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-6">
              <label className="text-xs text-[var(--ink-2)]">Body</label>
              <textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={3}
                data-testid="form-body"
                className="w-full mt-1 px-3 py-2 rounded-lg border text-sm"
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs text-[var(--ink-2)]">CTA link (e.g. /app/admin/podcasters)</label>
              <input
                value={form.cta_link}
                onChange={(e) => setForm({ ...form, cta_link: e.target.value })}
                data-testid="form-cta-link"
                className="w-full mt-1 px-3 py-2 rounded-lg border text-sm"
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs text-[var(--ink-2)]">CTA label</label>
              <input
                value={form.cta_label}
                onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                data-testid="form-cta-label"
                className="w-full mt-1 px-3 py-2 rounded-lg border text-sm"
              />
            </div>
            <div className="md:col-span-6 flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_published}
                  onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                  data-testid="form-published"
                />
                Published (visible to users)
              </label>
              <div className="flex gap-2">
                <button onClick={cancelEdit} className="btn-secondary text-sm">
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="form-save-btn"
                  className="btn-primary text-sm flex items-center gap-2"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[var(--ink-2)] flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : entries.length === 0 ? (
          <div className="p-10 text-center text-[var(--ink-2)]">
            No entries yet — click <strong>Seed historical</strong> to import the
            last 8 shipped features, or <strong>New entry</strong> to write one from scratch.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="entries-table">
            <thead className="bg-slate-50 text-xs uppercase text-[var(--ink-2)]">
              <tr>
                <th className="text-left p-3 w-8"></th>
                <th className="text-left p-3">Entry</th>
                <th className="text-left p-3">Tag</th>
                <th className="text-left p-3">Published</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t hover:bg-slate-50" data-testid={`entry-row-${e.id}`}>
                  <td className="p-3 text-xl">{e.emoji}</td>
                  <td className="p-3">
                    <div className="font-semibold">{e.title}</div>
                    <div className="text-xs text-[var(--ink-2)] line-clamp-1">{e.body}</div>
                    <div className="text-[10px] text-[var(--ink-2)] mt-0.5">
                      {new Date(e.published_at).toLocaleString()}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100">{e.tag}</span>
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => togglePublished(e)}
                      data-testid={`toggle-published-${e.id}`}
                      className={`text-xs px-2 py-1 rounded inline-flex items-center gap-1 ${e.is_published ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}
                    >
                      {e.is_published ? <Eye size={12} /> : <EyeOff size={12} />}
                      {e.is_published ? "Live" : "Hidden"}
                    </button>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => startEdit(e)}
                        data-testid={`edit-${e.id}`}
                        className="text-xs px-2 py-1 rounded hover:bg-slate-100"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => remove(e)}
                        data-testid={`delete-${e.id}`}
                        className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
